import 'moment/locale/es';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { CustomResponseInterface } from '@common/interfaces';
import { getFormattedDni, ProductDTO, SaleRegistryModel } from '@modules/pos/models';
import { SortEvent } from '@modules/tables/directives';
import { environment } from 'environments/environment';
import moment from 'moment';
import { BehaviorSubject, finalize, Observable, tap } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class BillsService {
    private API_URL = environment.API_URL;
    private _bill$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private _billNumber$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    loading: boolean = true;
    searchControl = new FormControl('');
    billsCount$: BehaviorSubject<number> = new BehaviorSubject<any>(null);

    constructor(private http: HttpClient) {}

    get bill$() {
        return this._bill$;
    }

    set bill(bill: any) {
        this._bill$.next(bill);
    }

    get billNumber$() {
        return this._billNumber$;
    }

    set billNumber(billNumber: number) {
        this._billNumber$.next(billNumber);
    }

    pageSize: number = 50;
    currentPage: number = 1;
    totalPages: number = 1;
    totalDocs: number = 0;
    sortedColumn: string = 'updatedAt';
    sortedDirection: string = 'desc';

    getSalesRegistries$(fromDate?: any, toDate?: any): Observable<any> {
        this.loading = true;

        let params = new HttpParams();
        params = params.append('fromDate', fromDate);
        params = params.append('toDate', toDate);
        params = params.append('search', this.searchControl.value);

        params = params.append('currentPage', this.currentPage);
        params = params.append('pageSize', this.pageSize);
        params = params.append('sortedColumn', this.sortedColumn);
        params = params.append('sortedDirection', this.sortedDirection);

        return this.http.get<any>(`${this.API_URL}/pos/bills`, { params: params }).pipe(
            tap(res => {
                this.totalDocs = res.totalDocs;
                this.totalPages = res.totalPages;
            }),
            finalize(() => {
                this.loading = false;
            }),
        );
    }

    getSalesRegistriesWithFilters$(filters: {
        fromDate?: any;
        toDate?: any;
        search?: string;
        salesChannelId?: string;
        customerId?: string;
        paymentMethods?: string[];
        productId?: string;
        categoryId?: string;
        sku?: string;
        userId?: string;
        taxes?: string[];
    }): Observable<any> {
        this.loading = true;

        let params = new HttpParams();
        params = params.append('fromDate', filters.fromDate);
        params = params.append('toDate', filters.toDate);
        params = params.append('search', filters.search || this.searchControl.value);

        if (filters.salesChannelId) params = params.append('salesChannelId', filters.salesChannelId);
        if (filters.customerId) params = params.append('customerId', filters.customerId);
        if (filters.paymentMethods && filters.paymentMethods.length > 0) {
            params = params.append('paymentMethods', filters.paymentMethods.join(','));
        }
        if (filters.productId) params = params.append('productId', filters.productId);
        if (filters.categoryId) params = params.append('categoryId', filters.categoryId);
        if (filters.sku) params = params.append('sku', filters.sku);
        if (filters.userId) params = params.append('userId', filters.userId);
        if (filters.taxes && filters.taxes.length > 0) {
            params = params.append('taxes', filters.taxes.join(','));
        }

        params = params.append('currentPage', this.currentPage);
        params = params.append('pageSize', this.pageSize);
        params = params.append('sortedColumn', this.sortedColumn);
        params = params.append('sortedDirection', this.sortedDirection);

        return this.http.get<any>(`${this.API_URL}/pos/bills`, { params: params }).pipe(
            tap(res => {
                this.totalDocs = res.totalDocs;
                this.totalPages = res.totalPages;
            }),
            finalize(() => {
                this.loading = false;
            }),
        );
    }

    /**
     * Obtiene las opciones de filtro (clientes y usuarios) para el rango de fechas.
     * Se llama al cargar el componente y al cambiar las fechas.
     */
    getFilterOptions$(fromDate: string, toDate: string): Observable<any> {
        let params = new HttpParams();
        params = params.append('fromDate', fromDate);
        params = params.append('toDate', toDate);
        return this.http.get<any>(`${this.API_URL}/pos/bills/filter-options`, { params });
    }

    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
        }
    }

    goToPage(page: number) {
        if (this.currentPage !== page) {
            this.currentPage = page;
        }
    }

    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
        }
    }

    search() {
        this.currentPage = 1;
    }

    onSort({ column, direction }: SortEvent) {
        this.sortedColumn = column;
        this.sortedDirection = direction;
        this.currentPage = 1;
    }

    getSaleRegistrieById$(billId: string): Observable<CustomResponseInterface<SaleRegistryModel>> {
        return this.http.get<any>(`${this.API_URL}/pos/bills/billById/${billId}`);
    }

    getBillNumber$(): Observable<any> {
        return this.http.get<any>(`${this.API_URL}/pos/bills/number`);
    }

    updateAllStatus$(): Observable<any> {
        return this.http.put<any>(`${this.API_URL}/pos/bills/update-all-status`, {});
    }

    updateStatus$(billId: String, status: number): Observable<any> {
        return this.http.put<any>(`${this.API_URL}/pos/bills/update-status/${billId}`, { status });
    }

    deleteSaleById(orderId: string): Observable<any> {
        return this.http.delete<any>(`${this.API_URL}/reverse-operations/reverse-order/${orderId}`);
    }

    returnSaleById(orderId: string, orderData: ProductDTO): Observable<CustomResponseInterface<any>> {
        return this.http.put<any>(`${this.API_URL}/reverse-operations/return-order/${orderId}`, orderData);
    }

    /**
     * Preview server-side de una devolución/cambio: el backend recalcula la venta resultante
     * (descuento prorrateado, cargos extra, IVA/IGTF) y devuelve la diferencia a devolver/cobrar,
     * el tope y los ítems netos para la nota fiscal. Es la fuente de verdad de los montos: la UI
     * no debe calcularlos localmente.
     */
    previewReturnSale(orderId: string, orderData: ProductDTO): Observable<CustomResponseInterface<any>> {
        return this.http.post<any>(`${this.API_URL}/reverse-operations/return-order/${orderId}/preview`, orderData);
    }

    getBillCount$(): Observable<any> {
        return this.http.get<any>(`${this.API_URL}/pos/bills/count`);
    }

    getBankAccount$(): Observable<any> {
        return this.http.get<any>(`${this.API_URL}/pos/bills/bank-account`);
    }

    updateBillCustomer$(billId: string, customerId: string, customerData: any): Observable<any> {
        return this.http.put<any>(`${this.API_URL}/pos/bills/${billId}/customer`, { customerId, customerData });
    }

    printPdfBill(bill: any) {
        const PDF = `<div class="col-md-12">
        <div class="row">

               <div class="receipt-main col-12">
                   <div class="row">
                       <div class="receipt-header col-6">
                           <div class="col-xs-6 col-sm-6 col-md-6">
                               <div class="receipt-left">
                                   <img class="img-responsive" alt="iamgurdeeposahan" src="https://bootdey.com/img/Content/avatar/avatar6.png" style="width: 71px; border-radius: 43px;">
                               </div>
                           </div>
                           <div class="my-3">
                               <div class="receipt-right">
                                <h5>Nombre: ${bill?.customer?.name || ''} </h5>
                                <p><b>Cédula :</b> ${getFormattedDni(bill?.customer)}</p>

                               </div>
                           </div>
                       </div>

                       <div class="receipt-header receipt-header-mid col-6">
                           <div class="text-right">
                                <div class="receipt-right">
                                     <h5>Vendedor: <strong>${bill?.user?.name ?? ''} ${bill?.user?.lastName ?? ''}</strong></h5>
                               </div>
                           </div>
                       </div>
                   </div>

                   <div>
                       <table class="table table-bordered">
                           <thead>
                               <tr>
                                   <th>Description</th>
                                   <th>Amount</th>
                               </tr>
                           </thead>
                           <tbody>
                                ${bill.sale.salesChannel.products
                                    .map((e: any) => {
                                        const product = bill.sale.products.map((item: any) => {
                                            if (item == e.product) {
                                                return e;
                                            }
                                        });

                                        return product[0]
                                            ? `<tr>
                                        <td class="col-md-9">${product[0]?.name}</td>
                                        <td class="col-md-3"><i class="fa fa-inr"></i> ${product[0]?.sellingPrice}</td>
                                    </tr>
                                    `
                                            : '';
                                    })
                                    .join('')}

                               <tr>
                                   <td class="text-right">
                                   <p>
                                       <strong>SubTotal: </strong>
                                   </p>
                                   <p>
                                       <strong>IVA: </strong>
                                   </p>
                                   <p>
                                       <strong>IGTF: </strong>
                                   </p>
                                   <p>
                                       <strong>Tasa de Cambio: </strong>
                                   </p>
                                   </td>
                                   <td>
                                   <p>
                                       <strong><i class="fa fa-inr"></i> ${bill.subtotal}</strong>
                                   </p>
                                   <p>
                                       <strong><i class="fa fa-inr"></i> ${bill.iva}</strong>
                                   </p>
                                   <p>
                                       <strong><i class="fa fa-inr"></i> ${bill.igtf}</strong>
                                   </p>
                                   <p>
                                       <strong><i class="fa fa-inr"></i> ${bill.exchangeRate} Bs./$</strong>
                                   </p>
                                   </td>
                               </tr>
                               <tr>

                                   <td class="text-right">
                                   <h2><strong>Total USD: </strong></h2>
                                   <p><strong>Total VES: </strong></p>
                                   </td>
                                   <td class="text-left text-danger">
                                   <h2><strong><i class="fa fa-inr"></i>${bill.totalPriceUSD}</strong></h2>
                                   <p><strong><i class="fa fa-inr"></i>${bill.totalPriceVES}</strong></p>
                                   </td>
                               </tr>

                               <tr>
                                   <td class="text-right">
                                   <h3><strong>Metodo de pagos: </strong></h3>
                                   </td>
                                   <td class="text-left text-danger"
                                   >
                                   ${bill.paymentMethods
                                       .map((e: any) => {
                                           return `<p class="mb-0"><strong>Nombre:</strong> ${e.name}</br>
                                            <strong>Monto:</strong> ${e.currency.code === 'USD' ? e.amountUSD + '$' : e.amountVES + 'VES'}</p>

                                    `;
                                       })
                                       .join('<br>')}
                                   </td>
                               </tr>
                           </tbody>
                       </table>
                   </div>

                   <div class="row">
                       <div class="receipt-header receipt-header-mid receipt-footer">
                           <div class="text-left">
                               <div class="receipt-right">
                                   <p><b>Fecha de registro :</b> ${moment(bill.createdAt).format('DD-MM-YYYY, h:mm a')}</p>
                                   <h5 style="color: rgb(140, 140, 140);">Gracias por su compra.!</h5>
                               </div>
                           </div>
                       </div>
                   </div>

               </div>
           </div>
       </div>`;

        return PDF;
    }

    printPdfStyles() {
        const styles = `<style>
        body{
                background:#eee;
                margin-top:20px;
                }

                .text-danger strong {
                    color: #9f181c;
                }

                .receipt-main thead {
                    background: #414143 none repeat scroll 0 0 !important;
                }

                .receipt-main {
                    background: #ffffff none repeat scroll 0 0;
                    border-bottom: 12px solid #333333;
                    border-top: 12px solid #9f181c;
                    margin-top: 50px;
                    margin-bottom: 50px;
                    padding: 40px 30px !important;
                    position: relative;
                    box-shadow: 0 1px 21px #acacac;
                    color: #333333;
                    font-family: open sans;
                }

                .receipt-main p {
                    color: #333333;
                    font-family: open sans;
                    line-height: 1.42857;
                }

                .receipt-footer h1 {
                    font-size: 15px;
                    font-weight: 400 !important;
                    margin: 0 !important;
                }

                .receipt-main::after {
                    background: #414143 none repeat scroll 0 0;
                    content: "";
                    height: 5px;
                    left: 0;
                    position: absolute;
                    right: 0;
                    top: -13px;
                }

                .receipt-main thead th {
                    color:#fff;
                }

                .receipt-right h5 {
                    font-size: 16px;
                    font-weight: bold;
                    margin: 0 0 7px 0;
                }

                .receipt-right p {
                    font-size: 12px;
                    margin: 0px;
                }

                .receipt-right p i {
                    text-align: center;
                    width: 18px;
                }

                .receipt-main td {
                    padding: 9px 20px !important;
                }

                .receipt-main th {
                    padding: 13px 20px !important;
                }

                .receipt-main td {
                    font-size: 13px;
                    font-weight: initial !important;
                }

                .receipt-main td p:last-child {
                    margin: 0;
                    padding: 0;
                }

                .receipt-main td h2 {
                    font-size: 20px;
                    font-weight: 900;
                    margin: 0;
                    text-transform: uppercase;
                }

                .receipt-header-mid .receipt-left h1 {
                    font-weight: 100;
                    margin: 34px 0 0;
                    text-align: right;
                    text-transform: uppercase;
                }

                .receipt-header-mid {
                    margin: 24px 0;
                    overflow: hidden;
                }

                #container {
                    background-color: #dcdcdc;
                }
      </style>`;

        return styles;
    }
}
